// Handoff por WhatsApp após o pedido. Constrói um href SEGURO (bloqueia
// javascript:/data:) a partir do campo `whatsapp` do tenant (telefone OU link) e
// de uma mensagem com os itens/entrega/pagamento/total, URL-encoded.

import { safeExternalUrl } from './url';
import { formatBRL } from './format';

/** Normaliza o campo whatsapp (telefone ou link) numa base https segura, ou null. */
export function whatsappBase(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const t = String(raw).trim();
  if (!t) return null;
  // Telefone: só dígitos, +, espaços e pontuação de telefone.
  if (/^\+?[\d\s().-]{8,}$/.test(t)) {
    let d = t.replace(/\D/g, '');
    if (!d) return null;
    // Número BR de até 11 dígitos = DDD + assinante, SEMPRE precisa do DDI 55 (só 12–13 dígitos já o contêm).
    // O `!startsWith('55')` anterior quebrava o DDD 55 (RS: Santa Maria/Uruguaiana etc.), que virava um
    // wa.me sem país válido (QA lote2).
    if (d.length <= 11) d = '55' + d; // assume BR sem código de país
    return `https://wa.me/${d}`;
  }
  // Caso contrário, trata como URL e sanitiza o esquema.
  return safeExternalUrl(t);
}

export interface WhatsappOrderInfo {
  shopName: string;
  numero: number | string;
  itens: { qty: number; nome: string; variacao?: string | null; adicionais?: string[] }[];
  entregaLabel: string;
  pagamentoLabel: string;
  total: number;
}

/** Mensagem legível do pedido para o WhatsApp. */
export function buildOrderMessage(info: WhatsappOrderInfo): string {
  const linhas = info.itens
    .map((i) => {
      const extras = [i.variacao, ...(i.adicionais ?? [])].filter(Boolean).join(', ');
      return `• ${i.qty}x ${i.nome}${extras ? ` (${extras})` : ''}`;
    })
    .join('\n');
  return (
    `Olá, ${info.shopName}! Acabei de fazer o pedido *#${info.numero}* pelo cardápio digital.\n\n` +
    `${linhas}\n\n` +
    `${info.entregaLabel} · Pagamento: ${info.pagamentoLabel}\n` +
    `Total: ${formatBRL(info.total)}`
  );
}

/**
 * href final do WhatsApp (base segura + ?text=mensagem). Retorna null se não há
 * whatsapp ou se o valor não passa na sanitização (ex.: javascript:/data:).
 */
export function buildWhatsappHref(
  raw: string | null | undefined,
  info: WhatsappOrderInfo
): string | null {
  const base = whatsappBase(raw);
  if (!base) return null;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}text=${encodeURIComponent(buildOrderMessage(info))}`;
}
